<template>
    <MeetingLayout v-bind="meetingSharedData.getLayoutProps(errorMessage)">
        <template #title>Reschedule or cancel meeting</template>
        <template #content>
            <div class="max-w-3xl mx-auto space-y-4">
                <div
                    v-if="localBookingData.isRecurring()"
                    class="flex justify-end"
                >
                    <span
                        @click="scrollToCancelAllButton()"
                        class="text-sm text-gray-600 cursor-pointer"
                    >
                        Cancel all meetings
                    </span>
                </div>
                <MeetingCard
                    v-for="meetingData in localBookingData.meetingsData"
                    :meetingData="meetingData"
                    :attendanceData="
                        meetingData.getSingleExistingAttendanceData()
                    "
                    :reservationData="
                        meetingData.getSingleExistingReservationData()
                    "
                    @onMeetingUpdated="handleMeetingUpdated($event.meetingData)"
                    @onError="handleError($event.errorMessage)"
                >
                    <template #actions>
                        <AttendanceRescheduleButtonDrawer
                            :meeting="meetingData.meeting"
                            :attendance="
                                meetingData.getSingleExistingAttendanceData()
                                    .attendance
                            "
                            :reservation="
                                meetingData.getSingleExistingReservationData()
                                    .reservation
                            "
                            @onSuccess="
                                handleMeetingUpdated($event.meetingData)
                            "
                        />
                        <AttendanceCancelButtonModal
                            :meeting="meetingData.meeting"
                            :attendanceData="
                                meetingData.getSingleExistingAttendanceData()
                            "
                            @onSuccess="
                                handleMeetingUpdated($event.meetingData)
                            "
                            @onError="handleError($event.errorMessage)"
                        />
                    </template>
                </MeetingCard>
                <div
                    v-if="localBookingData.isRecurring()"
                    :id="CANCEL_ALL_BUTTON"
                    class="flex justify-center"
                >
                    <RecurringMeetingCancelButtonModal
                        :bookingData="localBookingData"
                        @onSuccess="handleBookingUpdated($event.bookingData)"
                        @onError="handleError($event.errorMessage)"
                    />
                </div>
            </div>
        </template>
    </MeetingLayout>
</template>

<script setup lang="ts">
    import { ref } from 'vue'
    import MeetingLayout from '@/Shared/Layout/Meeting/MeetingLayout.vue'
    import MeetingCard from '@/BookingForm/BookingForm/Component/MeetingCard.vue'
    import MeetingSharedData from '@/Shared/Entity/MeetingSharedData'
    import BookingData from '@/Meeting/Booking/Entity/BookingData'
    import MeetingData from '@/App/Booking/Meeting/Entity/MeetingData'
    import RecurringMeetingCancelButtonModal from '@/Meeting/Recurring/Component/RecurringMeetingCancelButtonModal.vue'
    import usePageScroll from '@/Shared/Hook/usePageScroll'
    import AttendanceRescheduleButtonDrawer from '@/Meeting/Meeting/Component/AttendanceRescheduleButtonDrawer.vue'
    import AttendanceCancelButtonModal from '@/Meeting/Meeting/Component/AttendanceCancelButtonModal.vue'

    interface ShowMeetingProps {
        meetingSharedDataData: any
        bookingDataData: any
    }

    const props = defineProps<ShowMeetingProps>()
    const meetingSharedData = MeetingSharedData.hydrate(
        props.meetingSharedDataData,
    )
    const localBookingData = ref<BookingData>(
        BookingData.hydrateSingle(props.bookingDataData),
    )
    const errorMessage = ref<string | null>(null)

    const handleError = (message: string | null) => {
        errorMessage.value = message

        usePageScroll().scrollToTop()
    }

    const handleMeetingUpdated = (updatedMeetingData: MeetingData): void => {
        localBookingData.value =
            localBookingData.value.replaceUpdatedMeetingData(updatedMeetingData)
    }

    const handleBookingUpdated = (updatedBookingData: BookingData): void => {
        localBookingData.value = updatedBookingData
    }

    // ------------
    // Scroll to bottom
    // ------------

    const CANCEL_ALL_BUTTON = 'CANCEL_ALL_BUTTON'

    const scrollToCancelAllButton = (): void => {
        usePageScroll().scrollToElement(CANCEL_ALL_BUTTON)
    }
</script>
